import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { getSelectedTexts, getTexts } from '../../screens/TextSet/api';
import { getTags } from '../../screens/WordSet/api';
import { CHANGE_PAGE, OUTER_STATE } from './constants';
import { logError } from '@/utils/logHandling';

export const TextsetContext = createContext();

const DEFAULT_VALUE = {
  term: '',
  termType: 'text',
  complexity: 'einfach',
  textLength: 'kurz',
  searchTextsPerPage: 30,
  page: 1,
};

const reducer = (state = DEFAULT_VALUE, action) => {
  const { type, payload } = action;
  switch (type) {
    case OUTER_STATE: {
      const { name, value } = payload;
      return {
        ...state,
        page: 1,
        [name]: value,
      };
    }
    case CHANGE_PAGE: {
      const { value, arrowName } = payload;
      if (arrowName === null) {
        return {
          ...state,
          page: value,
        };
      } else {
        if (arrowName === 'right') {
          return {
            ...state,
            page: parseInt(state.page) + 1,
          };
        } else {
          return {
            ...state,
            page: parseInt(state.page) - 1,
          };
        }
      }
    }
    default: {
      return state;
    }
  }
};

export function TextsetContextProvider({
  children,
  patientId,
  setId,
  apiName,
}) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_VALUE);
  // on pagination press, we want to scroll up in the sets
  const scrollViewRef = useRef(null);

  const [data, setData] = useState({
    texts: [],
    totalCount: 0,
  });
  const [tags, setTags] = useState(null);
  const [limitModal, setLimitModal] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const componentMounted = useRef(false);
  const requestRef = useRef(null);
  const editData = useRef(null);

  const handleOuterState = useCallback((name, value) => {
    dispatch({
      type: OUTER_STATE,
      payload: { name, value },
    });
  }, []);

  const changePage = useCallback((value, arrowName = null) => {
    dispatch({
      type: CHANGE_PAGE,
      payload: { value, arrowName },
    });
    if (scrollViewRef.current && value) {
      scrollViewRef.current.scrollTo({ y: 300, animated: true });
    }
  }, []);

  const changeLimitModal = useCallback(() => {
    setLimitModal((prevState) => !prevState);
  }, []);

  const changeSelectedData = useCallback((newSelectedData) => {
    setSelectedData(newSelectedData);
  }, []);

  const changeData = useCallback((newData) => {
    setData(newData);
  }, []);

  const handleFetchTexts = useCallback(async (object) => {
    try {
      // eslint-disable-next-line no-unused-vars
      let payload;
      if (object?.termType === 'text' || object?.termType === 'tag') {
        payload = { ...object, term: object?.term.trim() };
      } else {
        payload = { ...object };
      }
      const { textLength, ...rest } = payload;
      const { data } = await getTexts(rest);
      const textsWithLength = data.texts.map((text) => {
        return {
          ...text,
          textLength,
        };
      });
      data.texts = textsWithLength;
      setData(data);
    } catch (error) {
      logError('Could not fetch texts', error);
    }
  }, []);

  const handleFetchSelectedTexts = useCallback(
    async (apiName, patientId, setId) => {
      try {
        const { data } = await getSelectedTexts(apiName, patientId, setId);
        editData.current = data;
        const { texts } = data;
        setSelectedData(texts);
      } catch (error) {
        logError('Could not fetch selected texts', error);
      }
    },
    [],
  );

  const handleFetchTags = useCallback(async () => {
    try {
      const { data } = await getTags();
      const { texts } = data;
      setTags({ texts });
    } catch (error) {
      logError('Could not fetch tags', error);
    }
  });

  const refetchTexts = useCallback(() => {
    handleFetchTexts(state);
  }, [state]);

  useEffect(() => {
    if (!componentMounted.current) return;
    handleFetchTexts(state);
  }, [state.page, state.termType, state.complexity, state.textLength]);

  useEffect(() => {
    if (!componentMounted.current) return;
    clearTimeout(requestRef.current);
    requestRef.current = setTimeout(() => {
      handleFetchTexts(state);
    }, 500);
  }, [state.term]);

  useEffect(() => {
    const fetchTexts = async () => {
      if (!setId) {
        handleFetchTexts(state);
        handleFetchTags();
      } else {
        try {
          handleFetchTags();
          handleFetchTexts(state);
          handleFetchSelectedTexts(apiName, patientId, setId);
        } catch (error) {
          logError('Could not fetch ', error);
        }
      }
      componentMounted.current = true;
    };
    fetchTexts();
  }, []);

  const value = {
    data,
    selectedData,
    limitModal,
    editData: editData.current,
    page: state.page,
    complexity: state.complexity,
    searchTextsPerPage: state.searchTextsPerPage,
    term: state.term,
    termType: state.termType,
    textLength: state.textLength,
    handleOuterState,
    changeData,
    changeSelectedData,
    changePage,
    changeLimitModal,
    refetchTexts,
    patientId,
    setId,
    apiName,
    scrollViewRef,
    tags:
      tags?.texts?.map((item) => {
        return item.displayHierarchy;
      }) ?? [],
  };

  return (
    <TextsetContext.Provider value={value}>{children}</TextsetContext.Provider>
  );
}
